"use client";

import { supabase } from "@/lib/supabase";
import type { DbGlobalModel } from "@/types/db";
import { useCallback, useEffect, useState } from "react";

const GLOBAL_MODEL_SELECT = [
  "id",
  "model_id",
  "name",
  "provider",
  "tier",
  "description",
  "tagline",
  "pricing",
  "context_window",
  "is_active",
  "created_at",
  "created_by",
  "updated_by",
].join(", ");

type GlobalModelMutableFields = Pick<
  DbGlobalModel,
  | "model_id"
  | "name"
  | "provider"
  | "tier"
  | "description"
  | "tagline"
  | "pricing"
  | "context_window"
  | "is_active"
>;

export type GlobalModelUpdateInput = Partial<GlobalModelMutableFields> & {
  updated_by?: string | null;
};

export type GlobalModelCreateInput = GlobalModelMutableFields & {
  created_by?: string | null;
  updated_by?: string | null;
};

const sortModels = (items: DbGlobalModel[]) =>
  [...items].sort(
    (left, right) =>
      left.provider.localeCompare(right.provider) ||
      left.name.localeCompare(right.name)
  );

export const useGlobalModels = () => {
  const [models, setModels] = useState<DbGlobalModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);

    const { data, error: queryError } = await supabase
      .from("global_models")
      .select(GLOBAL_MODEL_SELECT)
      .order("provider", { ascending: true })
      .order("name", { ascending: true });

    if (queryError) {
      setError(queryError.message);
      setModels([]);
      setLoading(false);
      return;
    }

    setModels(sortModels((data ?? []) as unknown as DbGlobalModel[]));
    setLoading(false);
  }, []);

  const createModel = useCallback(async (input: GlobalModelCreateInput) => {
    const { data, error: createError } = await supabase
      .from("global_models")
      .insert(input)
      .select(GLOBAL_MODEL_SELECT)
      .single();

    if (createError) {
      return { data: null, error: createError.message };
    }

    const createdModel = data as unknown as DbGlobalModel;
    setModels((current) => sortModels([...current, createdModel]));

    return { data: createdModel, error: null };
  }, []);

  const updateModel = useCallback(
    async (id: string, updates: GlobalModelUpdateInput) => {
      const { data, error: updateError } = await supabase
        .from("global_models")
        .update(updates)
        .eq("id", id)
        .select(GLOBAL_MODEL_SELECT)
        .single();

      if (updateError) {
        return { data: null, error: updateError.message };
      }

      const updatedModel = data as unknown as DbGlobalModel;

      setModels((current) =>
        sortModels(
          current.map((model) =>
            model.id === updatedModel.id ? updatedModel : model
          )
        )
      );

      return { data: updatedModel, error: null };
    },
    []
  );

  const deleteModel = useCallback(async (id: string) => {
    const { error: deleteError } = await supabase
      .from("global_models")
      .delete()
      .eq("id", id);

    if (deleteError) {
      return { error: deleteError.message };
    }

    setModels((current) => current.filter((model) => model.id !== id));

    return { error: null };
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh();
  }, [refresh]);

  return {
    models,
    loading,
    error,
    refresh,
    createModel,
    updateModel,
    deleteModel,
  };
};
